// src/core/score.ts
import type { Hsl, Lab, ChannelErrors } from '../types';
import { circularDelta } from './color';

// CIE ΔE2000. Reference: Sharma, Wu, Dalal (2005).
export function deltaE2000(lab1: Lab, lab2: Lab): number {
  const { L: L1, a: a1, b: b1 } = lab1;
  const { L: L2, a: a2, b: b2 } = lab2;

  const avgL = (L1 + L2) / 2;
  const C1 = Math.sqrt(a1 * a1 + b1 * b1);
  const C2 = Math.sqrt(a2 * a2 + b2 * b2);
  const avgC = (C1 + C2) / 2;

  const G = 0.5 * (1 - Math.sqrt(Math.pow(avgC, 7) / (Math.pow(avgC, 7) + Math.pow(25, 7))));
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;

  const C1p = Math.sqrt(a1p * a1p + b1 * b1);
  const C2p = Math.sqrt(a2p * a2p + b2 * b2);
  const avgCp = (C1p + C2p) / 2;

  const h1p = hueAngle(b1, a1p);
  const h2p = hueAngle(b2, a2p);

  let dhp = h2p - h1p;
  if (Math.abs(dhp) > 180) dhp -= Math.sign(dhp) * 360;

  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * Math.PI) / 360);

  let avgHp;
  if (Math.abs(h1p - h2p) > 180) avgHp = (h1p + h2p + 360) / 2;
  else avgHp = (h1p + h2p) / 2;

  const T = 1
    - 0.17 * Math.cos(deg2rad(avgHp - 30))
    + 0.24 * Math.cos(deg2rad(2 * avgHp))
    + 0.32 * Math.cos(deg2rad(3 * avgHp + 6))
    - 0.20 * Math.cos(deg2rad(4 * avgHp - 63));

  const dTheta = 30 * Math.exp(-Math.pow((avgHp - 275) / 25, 2));
  const Rc = 2 * Math.sqrt(Math.pow(avgCp, 7) / (Math.pow(avgCp, 7) + Math.pow(25, 7)));
  const Sl = 1 + (0.015 * Math.pow(avgL - 50, 2)) / Math.sqrt(20 + Math.pow(avgL - 50, 2));
  const Sc = 1 + 0.045 * avgCp;
  const Sh = 1 + 0.015 * avgCp * T;
  const Rt = -Math.sin(deg2rad(2 * dTheta)) * Rc;

  return Math.sqrt(
    Math.pow(dLp / Sl, 2) +
    Math.pow(dCp / Sc, 2) +
    Math.pow(dHp / Sh, 2) +
    Rt * (dCp / Sc) * (dHp / Sh)
  );
}

function hueAngle(b: number, ap: number): number {
  if (b === 0 && ap === 0) return 0;
  let h = (Math.atan2(b, ap) * 180) / Math.PI;
  if (h < 0) h += 360;
  return h;
}

function deg2rad(d: number): number { return (d * Math.PI) / 180; }

// Map perceptual ΔE to a 0–100 player score.
export function scoreFromDeltaE(de: number): number {
  if (de <= 1) return 100;
  if (de >= 30) return 0;
  return Math.round(100 * (1 - (de - 1) / 29));
}

// Per-HSL-channel error magnitudes (used for the perception profile).
export function channelErrors(target: Hsl, guess: Hsl): ChannelErrors {
  return {
    hue: circularDelta(target.h, guess.h),
    sat: Math.abs(target.s - guess.s),
    light: Math.abs(target.l - guess.l),
  };
}
